import { selectUserProjectTimeZone } from 'core/userContext';
import { RadioListField, TimeZonePickerField } from 'lib/iterable-hook-form/FormField';
import noop from 'lodash/noop';
import { OptionText } from 'modules/campaigns/editor/styles';
import { ScheduleType } from 'modules/domains/campaign';
import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { match } from 'ts-pattern';
import { FormField } from 'ui/formField';
import { NewTabLink } from 'ui/links';
import { LeftDiv } from 'ui/radioList/RadioListStyles';
import { Caption, fontSizes, spacing } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { ProjectTimeZone, RecipientTimeZonesWrapper } from '../ScheduleCampaignModal.styled';

const RecipientTimeZoneOption = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
`;

const StyledRadioListField = styled(RadioListField)`
  ${LeftDiv} {
    &:nth-child(1) {
      height: 100%;
      display: flex;
      align-items: flex-start;
      svg {
        margin: 0;
      }
    }
  }
`;

const StyledTimeZonePickerField = styled(TimeZonePickerField)`
  width: ${pxToRem(100)};
`;

const StyledNewTabLink = styled(NewTabLink)`
  font-size: ${fontSizes.s12};
  svg {
    height: ${spacing.threeQuarter};
    width: ${spacing.threeQuarter};
    margin-left: ${spacing.quarter};
  }
`;

export const ColumnWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: ${spacing.half};
`;

const getScheduleTypeOptions = (projectTimeZone = '', scheduleType: ScheduleType) => [
  {
    id: ScheduleType.ProjectTimeZone,
    value: ScheduleType.ProjectTimeZone,
    name: () => (
      <OptionText>
        Project time zone <ProjectTimeZone>({projectTimeZone})</ProjectTimeZone>
      </OptionText>
    ),
  },
  {
    id: ScheduleType.RecipientTimeZone,
    value: ScheduleType.RecipientTimeZone,
    name: () => {
      const isSelected = scheduleType === ScheduleType.RecipientTimeZone;

      return (
        <RecipientTimeZoneOption>
          <OptionText>Recipient&apos;s time zone</OptionText>
          {isSelected && (
            <Caption>
              If the timeZone field is set for the recipient, they&apos;ll receive it in their time
              zone. If start time is set in the past or isn&apos;t set, they&apos;ll receive the
              message in the fallback time zone. To learn more,{' '}
              <StyledNewTabLink href="https://support.iterable.com/hc/en-us/articles/7412316462996-Optimizing-Campaign-Delivery-Times-#sending-in-different-time-zones">
                visit our help center
              </StyledNewTabLink>
            </Caption>
          )}
        </RecipientTimeZoneOption>
      );
    },
  },
];

export const SendWithRespectTo = () => {
  const projectTimeZone = useSelector(selectUserProjectTimeZone);

  const { watch, trigger } = useFormContext();

  const scheduleType = watch('scheduleType');
  const timeZoneOptions = useMemo(
    () => getScheduleTypeOptions(projectTimeZone, scheduleType),
    [projectTimeZone, scheduleType],
  );

  useEffect(() => {
    watch((_, { name }) => {
      match(name)
        .with('scheduleType', () => {
          trigger('startDate');
          trigger('endDate');
          trigger('startTime');
        })
        .with('startingTimeZone', () => {
          trigger('startDate');
          trigger('endDate');
          trigger('startTime');
        })
        .otherwise(noop);
    });
  }, [watch, trigger]);

  return (
    <FormField labelText="Send with respect to:">
      <StyledRadioListField name="scheduleType" options={timeZoneOptions} />
      {scheduleType === ScheduleType.RecipientTimeZone && (
        <RecipientTimeZonesWrapper>
          <FormField labelText="Starting time zone">
            <ColumnWrapper>
              <StyledTimeZonePickerField name="startingTimeZone" />
              <Caption>Iterable won&apos;t send messages before this time.</Caption>
            </ColumnWrapper>
          </FormField>

          <FormField labelText="Fallback time zone">
            <ColumnWrapper>
              <StyledTimeZonePickerField name="fallbackTimeZone" />
              <Caption>
                Iterable uses this time zone to send a campaign for all users who do not have a
                timeZone on their user profile.
              </Caption>
            </ColumnWrapper>
          </FormField>
        </RecipientTimeZonesWrapper>
      )}
    </FormField>
  );
};
